import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal, Button, HiyaLogo } from "@hiya/ui";
import { signOut } from "../auth/auth-service";
import type { BadgeCounts } from "../types/orders";
import iconMoneyRecive from "../assets/icon-money-recive.svg";
import iconBoxSearch from "../assets/icon-box-search.svg";
import iconDocumentDownload from "../assets/icon-document-download.svg";
import iconDocumentUpload from "../assets/icon-document-upload.svg";
import iconNote2 from "../assets/icon-note-2.svg";
import iconUser from "../assets/icon-user.svg";
import iconNotification from "../assets/icon-notification.svg";
import iconLogout from "../assets/icon-logout.svg";
import iconArrowDown from "../assets/icon-arrow-down.svg";
import { IconBook } from "./icons/IconBook";
import { IconMolecule } from "./icons/IconMolecule";
import { IconDocumentCopy } from "./icons/IconDocumentCopy";
import { IconMessageQuestion } from "./icons/IconMessageQuestion";
import { IconNotificationStatus } from "./icons/IconNotificationStatus";

const CONTENT_SUB_ITEMS = [
  { label: "FAQ", path: "/dashboard/content/faqs", Icon: IconMessageQuestion },
  { label: "Explore Articles", path: "/dashboard/content/articles", Icon: IconBook },
  { label: "Hiya Decode", path: "/dashboard/content/decode", Icon: IconMolecule },
  { label: "Push Notifications", path: "/dashboard/push-notifications", Icon: IconNotificationStatus },
  { label: "Legal Pages", path: "/dashboard/content/legal-pages", Icon: IconDocumentCopy },
];

type BadgeKey = "orders" | "samples_at_lab" | "results_received" | "reports_published";

const NAV_ITEMS = [
  { label: "New Orders", path: "/dashboard/orders", icon: iconMoneyRecive, badgeKey: "orders" as BadgeKey },
  { label: "Samples at Lab", path: "/dashboard/samples", icon: iconBoxSearch, badgeKey: "samples_at_lab" as BadgeKey },
  { label: "Results Received", path: "/dashboard/results", icon: iconDocumentDownload, badgeKey: "results_received" as BadgeKey },
  { label: "Reports Published", path: "/dashboard/reports-published", icon: iconDocumentUpload, badgeKey: "reports_published" as BadgeKey },
  { label: "Content Management", path: "/dashboard/content", icon: iconNote2, expandable: true },
  { label: "Users", path: "/dashboard/users", icon: iconUser },
];

interface SidebarProps {
  email: string;
  badgeCounts?: BadgeCounts;
  unreadNotifications?: number;
}

export function Sidebar({ email, badgeCounts, unreadNotifications = 0 }: Readonly<SidebarProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogout, setShowLogout] = useState(false);
  const [contentExpanded, setContentExpanded] = useState(
    location.pathname.startsWith("/dashboard/content") ||
      location.pathname.startsWith("/dashboard/push-notifications"),
  );

  async function handleLogout() {
    try {
      await signOut();
    } catch {
      // sign-out best-effort; navigate regardless
    }
    navigate("/login", { replace: true });
  }

  return (
    <>
      <aside className="w-[280px] h-screen sticky top-0 border-r border-stroke-primary bg-bg-primary flex flex-col">
        <div className="flex flex-col gap-5 flex-1 overflow-y-auto min-h-0">
          <div className="pt-5 px-5">
            <HiyaLogo width={46} height={40} />
          </div>
          <nav className="flex flex-col gap-3">
            {NAV_ITEMS.map((item) => {
              const isActive = location.pathname.startsWith(item.path);
              const isExpandable =
                "expandable" in item && item.expandable;
              const badgeCount = "badgeKey" in item && item.badgeKey && badgeCounts
                ? badgeCounts[item.badgeKey]
                : 0;
              return (
                <div key={item.path}>
                  <button
                    onClick={() => {
                      if (isExpandable) {
                        setContentExpanded((prev) => !prev);
                      } else {
                        navigate(item.path);
                      }
                    }}
                    {...(isExpandable ? { "aria-expanded": contentExpanded } : {})}
                    className={`flex items-center gap-3 px-5 py-2 text-base font-medium transition-colors text-left w-[280px] ${
                      isActive
                        ? "border-l-2 border-solid border-l-brand-brown"
                        : ""
                    }`}
                  >
                    <div className="flex flex-1 gap-2 items-center">
                      <img src={item.icon} alt="" width={20} height={20} className="shrink-0" />
                      <span className="text-text-secondary">{item.label}</span>
                    </div>
                    {badgeCount > 0 && (
                      <span className="bg-brand-brown text-text-btn text-xs font-medium rounded-full size-5 flex items-center justify-center">
                        {badgeCount}
                      </span>
                    )}
                    {isExpandable && (
                      <img src={iconArrowDown} alt="" width={16} height={16} className="shrink-0" />
                    )}
                  </button>
                  {isExpandable && contentExpanded && (
                    <div className="flex flex-col">
                      {CONTENT_SUB_ITEMS.map((sub) => {
                        const isSubActive = location.pathname.startsWith(sub.path);
                        return (
                          <button
                            key={sub.path}
                            onClick={() => navigate(sub.path)}
                            className={`flex items-center gap-3 pl-8 pr-5 py-2 text-base font-medium transition-colors text-left w-[280px] ${
                              isSubActive
                                ? "border-l-2 border-solid border-l-brand-brown"
                                : ""
                            }`}
                          >
                            <div className={`flex flex-1 gap-2 items-center ${isSubActive ? "text-brand-brown" : "text-text-secondary"}`}>
                              <sub.Icon className="shrink-0" />
                              <span>{sub.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
        <div className="border-t border-stroke-primary py-2.5">
          <button
            onClick={() => navigate("/dashboard/notifications")}
            className={`flex items-center gap-3 px-5 py-2 text-base font-medium text-left w-[280px] ${
              location.pathname.startsWith("/dashboard/notifications")
                ? "border-l-2 border-solid border-l-brand-brown"
                : ""
            }`}
          >
            <div className="flex flex-1 gap-2 items-center">
              <img src={iconNotification} alt="" width={20} height={20} />
              <span className="text-text-secondary">Notifications</span>
            </div>
            {unreadNotifications > 0 && (
              <span className="bg-brand-brown text-text-btn text-xs font-medium rounded-full size-5 flex items-center justify-center">
                {unreadNotifications}
              </span>
            )}
          </button>
          <div className="flex items-center justify-between px-5 py-2">
            <span className="text-base font-medium text-text-primary truncate">
              {email}
            </span>
            <button
              onClick={() => setShowLogout(true)}
              className="shrink-0 hover:opacity-80 transition-opacity"
            >
              <img src={iconLogout} alt="Log out" width={20} height={20} />
            </button>
          </div>
        </div>
      </aside>

      <Modal open={showLogout} title="Log Out" onClose={() => setShowLogout(false)}>
        <p className="text-base text-text-secondary">
          Are you sure you want to log out?
        </p>
        <div className="flex gap-3 mt-2">
          <Button variant="secondary" onClick={() => setShowLogout(false)}>
            No, go back
          </Button>
          <Button onClick={handleLogout}>Yes, log out</Button>
        </div>
      </Modal>
    </>
  );
}
